import { z } from 'zod';

/**
 * ============================================================================
 * ANALYTICS EVENT CONTRACT  (tech-lead seam amendment, 2026-07-24)
 * ============================================================================
 * Typed event definitions per `docs/prd/metrics-plan.md §1`. CONTRACT ONLY — no
 * transport, no PostHog SDK, no `/e` server code lives here. The client implements
 * `AnalyticsEmitter`; the `/e` collector imports `parseEvent`/`safeParseEvent` to
 * RE-VALIDATE every incoming event before it stamps `ts_server` and forwards it.
 *
 * WHY A SHARED CONTRACT: §0 rule 6 — "every property is enumerated; no free-form
 * strings." A single Zod definition, imported by both the emitter and the
 * collector, is the only way "1,000 variants of the same event" can't happen and
 * the gate math (§2) stays computable.
 *
 * TWO RESOLVED AMBIGUITIES (documented decisions):
 *
 *  1. `ts_server` is SERVER-STAMPED ON INGEST (§0 rule 2). Therefore the CLIENT
 *     envelope must NOT contain it — this contract models the CLIENT payload (what
 *     the emitter sends / what `/e` receives). `.strict()` REJECTS a client-sent
 *     `ts_server`, so a client can never forge the authoritative timestamp. The
 *     collector adds `ts_server` after validation (a server concern, not modeled
 *     here). If a stored-event shape is later needed it is `AnalyticsEvent & {
 *     ts_server: string }`, added server-side.
 *
 *  2. `tile_type` is an OPAQUE identifier (§0 rule 6 lists it alongside the opaque
 *     `board_id`/`template_id`). The plan's examples even include ids not in the v1
 *     tile registry (e.g. `markets.watchlist`). So we DELIBERATELY do NOT couple
 *     this to the schema's `TILE_TYPES` union — that would make analytics brittle
 *     to taxonomy drift and reject historically-valid events. It is a bounded
 *     non-empty string (length-capped to bound cardinality, dotted-id shape).
 *
 * PRIVACY (mirrors §5 mandates, enforced at this boundary):
 *  - `email_sha256` is a 64-hex hash — plaintext email NEVER appears in an event
 *    (§1.12). The schema only accepts the hash.
 *  - `dnt` is stamped so QA can assert the fail-closed invariant (§0 rule 5); DNT
 *    users emit nothing, but when present it is a bool.
 *  - No IP, no cookie id — identity is the first-party `anon_id` only (§0 rule 1).
 * ============================================================================
 */

/* ------------------------------------------------------------------------- *
 * Enumerations (all closed sets — §0 rule 6)                                 *
 * ------------------------------------------------------------------------- */

/** Every event name in the taxonomy (§1). Stable forever once shipped (§1). */
export const ANALYTICS_EVENTS = [
  'page_loaded', // §1.2
  'tile_added', // §1.3
  'tile_removed', // §1.4
  'tile_moved', // §1.4
  'tile_resized', // §1.4
  'board_snapshot', // §1.5
  'board_customized', // §1.6 — the activation event
  'template_selected', // §1.7
  'edit_mode_opened', // §1.8
  'tile_configured', // §1.8
  'tile_detail_opened', // §1.9
  'clone_created', // §1.10 (Phase 2)
  'clone_visited', // §1.10 (Phase 2)
  'ext_installed', // §1.11 (Phase 2)
  'ext_uninstalled', // §1.11 (Phase 2)
  'pro_viewed', // §1.12 (Phase 2)
  'pro_clicked', // §1.12 (Phase 2)
  'pro_waitlist_joined', // §1.12 (Phase 2)
  // §1.S — Spaces feature (Phase 1 wave 2, 2026-07-27)
  'space_created', // §1.S.1
  'space_switched', // §1.S.2
  'space_renamed', // §1.S.3
  'space_reordered', // §1.S.4
  'space_deleted', // §1.S.5
  'space_duplicated', // §1.S.6
] as const;
export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[number];

const SURFACES = ['web', 'ext_newtab', 'tv'] as const; // §0 envelope
const VIEWPORTS = ['desktop', 'tablet', 'mobile'] as const; // §0 envelope
const LOAD_TYPES = ['cold', 'returning'] as const; // §1.2
const BOARD_SOURCES = ['default', 'persisted', 'clone', 'template'] as const; // §1.2
const ADD_METHODS = ['picker', 'template', 'clone', 'nl_builder'] as const; // §1.3
const TEMPLATE_IDS = ['commuter', 'sports_fan', 'investor', 'parent'] as const; // §1.7
const TEMPLATE_ENTRY = ['onboarding_question', 'template_gallery'] as const; // §1.7
const SETTING_CHANGED = ['sports_team', 'watchlist_symbols', 'news_topic', 'location', 'other'] as const; // §1.8
const DETAIL_DEPTH = ['detail', 'source'] as const; // §1.9
const BROWSERS = ['chrome', 'firefox'] as const; // §1.11
const PRO_VIEW_ENTRY = ['nav', 'tile_lock', 'upsell_banner', 'direct'] as const; // §1.12
const PRO_CLICK_ENTRY = ['nav', 'tile_lock', 'upsell_banner', 'direct'] as const; // §1.12
const PLANS = ['pro', 'family'] as const; // §1.12
const FEATURE_CONTEXT = ['alerts', 'ai_brief', 'premium_data', 'tv_mode', 'sync', 'none'] as const; // §1.12
/** Which op tipped the user into activation (§1.6). */
const TRIGGER_OPS = ['tile_added', 'tile_removed', 'tile_moved', 'tile_resized', 'tile_configured'] as const;
/** Which UI surface triggered a space switch (§1.S.2). */
const SPACE_SWITCH_METHODS = ['tab', 'pill', 'shortcut'] as const;
/** Which rename path was used (§1.S.3). */
const SPACE_RENAME_METHODS = ['inline', 'popover'] as const;

/* ------------------------------------------------------------------------- *
 * Shared field shapes                                                        *
 * ------------------------------------------------------------------------- */

/** Opaque, bounded identifiers (§0 rule 6). Non-empty, length-capped. */
const opaqueId = z.string().min(1).max(64);
/** `tile_type` — opaque dotted id, bounded (see §ambiguity 2 above). */
const tileType = z.string().min(1).max(48);
/** A grid slot `{x,y}` used by move events (§1.4). */
const gridSlot = z.object({ x: z.number().int().min(0), y: z.number().int().min(0) }).strict();
/** `display.size`-style token for resize before/after (§1.4). */
const sizeToken = z.string().regex(/^\d+x\d+$/);

/**
 * The envelope on EVERY event (§0 "Standard properties"). Client half only —
 * `ts_server` is added by the collector, NOT here (see ambiguity 1). Spread into
 * each event's strict object so unknown keys are rejected per-event.
 */
const envelopeShape = {
  anon_id: z.string().uuid(), // localStorage snip_anon_id (§0 rule 1)
  session_id: z.string().uuid(), // per-session (§1.1)
  ts_client: z.string().datetime(), // untrusted; debug only (§0 rule 2)
  surface: z.enum(SURFACES),
  app_version: z.string().min(1).max(64),
  is_returning: z.boolean(),
  viewport: z.enum(VIEWPORTS),
  dnt: z.boolean(),
} as const;

/** Build a strict event schema: discriminant `event` + envelope + the event's own props. */
function event<N extends AnalyticsEventName, S extends z.ZodRawShape>(name: N, props: S) {
  return z.object({ event: z.literal(name), ...envelopeShape, ...props }).strict();
}

/* ------------------------------------------------------------------------- *
 * Per-event schemas (§1.2–§1.12) — properties mirror the plan exactly        *
 * ------------------------------------------------------------------------- */

const pageLoaded = event('page_loaded', {
  load_type: z.enum(LOAD_TYPES),
  board_source: z.enum(BOARD_SOURCES),
  tile_count: z.number().int().min(0),
  has_geo: z.boolean(),
});

const tileAdded = event('tile_added', {
  tile_type: tileType,
  add_method: z.enum(ADD_METHODS),
  board_id: opaqueId,
  tile_count_after: z.number().int().min(0),
});

const tileRemoved = event('tile_removed', {
  tile_type: tileType,
  board_id: opaqueId,
  tile_count_after: z.number().int().min(0),
});

const tileMoved = event('tile_moved', {
  tile_type: tileType,
  board_id: opaqueId,
  from_slot: gridSlot,
  to_slot: gridSlot,
});

const tileResized = event('tile_resized', {
  tile_type: tileType,
  board_id: opaqueId,
  size_before: sizeToken,
  size_after: sizeToken,
});

const boardSnapshot = event('board_snapshot', {
  tile_count: z.number().int().min(0),
  mutated_since_default: z.boolean(),
  customization_ops: z.number().int().min(0),
  board_hash: opaqueId,
  /**
   * Total number of spaces at the time of the snapshot (§1.S enrichment).
   * Measures how many spaces users tend to accumulate.
   * OPTIONAL — version-skew tolerance for cached clients; enrichment is best-effort by design.
   * Old cached clients that do not send this field are still accepted by the collector.
   */
  space_count: z.number().int().min(1).optional(),
  /**
   * Zero-based index of the active space in the ordered spaces array (§1.S enrichment).
   * Helps understand which space slot users most frequently glance at.
   * OPTIONAL — version-skew tolerance for cached clients; enrichment is best-effort by design.
   * Old cached clients that do not send this field are still accepted by the collector.
   */
  active_space_index: z.number().int().min(0).optional(),
});

const boardCustomized = event('board_customized', {
  trigger_op: z.enum(TRIGGER_OPS),
  ms_since_page_load: z.number().int().min(0),
  tile_count: z.number().int().min(0),
});

const templateSelected = event('template_selected', {
  template_id: z.enum(TEMPLATE_IDS),
  entry_point: z.enum(TEMPLATE_ENTRY),
  replaced_board_hash: opaqueId,
});

const editModeOpened = event('edit_mode_opened', {
  board_id: opaqueId,
});

const tileConfigured = event('tile_configured', {
  tile_type: tileType,
  board_id: opaqueId,
  setting_changed: z.enum(SETTING_CHANGED),
});

const tileDetailOpened = event('tile_detail_opened', {
  tile_type: tileType,
  depth: z.enum(DETAIL_DEPTH),
});

const cloneCreated = event('clone_created', {
  board_id: opaqueId,
  board_hash: opaqueId,
  tile_count: z.number().int().min(0),
});

const cloneVisited = event('clone_visited', {
  source_board_id: opaqueId,
  is_new_visitor: z.boolean(),
});

const extInstalled = event('ext_installed', {
  browser: z.enum(BROWSERS),
});

const extUninstalled = event('ext_uninstalled', {
  browser: z.enum(BROWSERS),
  install_age_days: z.number().int().min(0),
});

const proViewed = event('pro_viewed', {
  entry_point: z.enum(PRO_VIEW_ENTRY),
});

const proClicked = event('pro_clicked', {
  plan: z.enum(PLANS),
  entry_point: z.enum(PRO_CLICK_ENTRY),
  feature_context: z.enum(FEATURE_CONTEXT),
});

const proWaitlistJoined = event('pro_waitlist_joined', {
  plan: z.enum(PLANS),
  feature_context: z.enum(FEATURE_CONTEXT),
  /** SHA-256 hex of the email — plaintext never appears in an event (§1.12). */
  email_sha256: z.string().regex(/^[a-f0-9]{64}$/),
});

// ---------------------------------------------------------------------------
// §1.S — Spaces events (Phase 1 wave 2, 2026-07-27)
// PRIVACY NOTE: space NAMES are user content and NEVER appear in any event.
// Only counts, indices, and bounded enums are emitted.
// ---------------------------------------------------------------------------

/**
 * Fired after a new space is successfully committed to persistence (§1.S.1).
 * `space_count` is the total after the create (always ≥ 2, since you started
 * with at least one).
 */
const spaceCreated = event('space_created', {
  space_count: z.number().int().min(2),
});

/**
 * Fired when the user explicitly switches to a different space (§1.S.2).
 * NOT fired for:
 *   - clicking the already-active space tab/pill
 *   - TV mode ?space= URL resolution (automatic, not user-initiated)
 *   - the auto-activate inside createSpace / deleteSpace (internal action)
 * `method` measures which navigation surface the user is using — directly
 * measures the value of the desktop tab strip vs the pill vs shortcuts.
 */
const spaceSwitched = event('space_switched', {
  method: z.enum(SPACE_SWITCH_METHODS),
  space_count: z.number().int().min(1),
});

/**
 * Fired after a space is successfully renamed and persisted (§1.S.3).
 * `method` distinguishes inline double-click rename from the popover dialog.
 */
const spaceRenamed = event('space_renamed', {
  method: z.enum(SPACE_RENAME_METHODS),
  space_count: z.number().int().min(1),
});

/**
 * Fired on drag-commit when the user reorders spaces (§1.S.4).
 * NOT fired on cancelled or reverted drags (drag.active === false on pointerUp).
 * `from_index` and `to_index` are zero-based positions in the ordered list.
 */
const spaceReordered = event('space_reordered', {
  from_index: z.number().int().min(0),
  to_index: z.number().int().min(0),
  space_count: z.number().int().min(1),
});

/**
 * Fired after the confirmed two-step delete commits and persists (§1.S.5).
 * `space_count` is the total AFTER the delete (always ≥ 1, since the last
 * space cannot be deleted).
 * `was_active` is true when the deleted space was the currently-active space
 * (measures how often users delete their current context).
 */
const spaceDeleted = event('space_deleted', {
  space_count: z.number().int().min(1),
  was_active: z.boolean(),
});

/**
 * Fired after a space is successfully duplicated and persisted (§1.S.6).
 * `space_count` is the total after the duplicate.
 */
const spaceDuplicated = event('space_duplicated', {
  space_count: z.number().int().min(1),
});

/* ------------------------------------------------------------------------- *
 * The discriminated union + parsers                                          *
 * ------------------------------------------------------------------------- */

/**
 * The full event contract — a discriminated union on `event`. `.strict()` per
 * option means an unknown `event` name OR any unknown extra key is REJECTED
 * (§0 rule 6; the collector's boundary guard). Parse, don't assume.
 */
export const AnalyticsEventSchema = z.discriminatedUnion('event', [
  pageLoaded,
  tileAdded,
  tileRemoved,
  tileMoved,
  tileResized,
  boardSnapshot,
  boardCustomized,
  templateSelected,
  editModeOpened,
  tileConfigured,
  tileDetailOpened,
  cloneCreated,
  cloneVisited,
  extInstalled,
  extUninstalled,
  proViewed,
  proClicked,
  proWaitlistJoined,
  // §1.S — Spaces events
  spaceCreated,
  spaceSwitched,
  spaceRenamed,
  spaceReordered,
  spaceDeleted,
  spaceDuplicated,
]);

/** A validated analytics event (client envelope). Discriminated on `event`. */
export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>;

/** Throwing parse — for trusted call sites. */
export function parseEvent(input: unknown): AnalyticsEvent {
  return AnalyticsEventSchema.parse(input);
}

/**
 * Non-throwing parse — the `/e` collector's boundary guard. Re-validates an
 * untrusted incoming payload before `ts_server` stamping + forwarding; a malformed
 * or unknown event is dropped, never forwarded (fail-closed on bad data).
 */
export function safeParseEvent(
  input: unknown,
): z.SafeParseReturnType<unknown, AnalyticsEvent> {
  return AnalyticsEventSchema.safeParse(input);
}

/* ------------------------------------------------------------------------- *
 * Emitter interface (client implements; contract only — no transport here)   *
 * ------------------------------------------------------------------------- */

/**
 * The fire-and-forget analytics sink the CLIENT implements (against PostHog EU +
 * the `/e` collector — metrics-plan §5). This contract fixes the SHAPE and the
 * behavioral guarantees; it deliberately carries NO transport.
 *
 * BEHAVIORAL CONTRACT (non-negotiable — mirrors §0 rules 4 & 5, §5):
 *  - `emit` is TOTAL and NON-BLOCKING: it MUST NOT throw and MUST NOT block the
 *    render path. A dropped/failed event is invisible to the user — analytics
 *    never degrades the product. Implementations return immediately (queue/beacon)
 *    and swallow transport errors internally (logging is their concern).
 *  - FAIL-CLOSED ON PRIVACY: if DNT is set (or required consent is absent), the
 *    implementation emits NOTHING — not even `page_loaded` (§0 rule 5). `enabled`
 *    lets callers cheaply check this before doing any per-event work.
 *  - The emitter is handed a CLIENT event (no `ts_server`); the collector stamps
 *    server time. Implementations SHOULD validate with `safeParseEvent` in dev to
 *    catch schema drift early, but must still never throw in prod.
 */
export interface AnalyticsEmitter {
  /**
   * Record one event. Fire-and-forget: returns synchronously, never throws, never
   * blocks. No-ops when `enabled` is false (DNT/consent — fail-closed).
   */
  emit(event: AnalyticsEvent): void;
  /** False when analytics is suppressed (DNT/consent-blocked); no event will send. */
  readonly enabled: boolean;
}
